import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Badge, Box, Button } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

import DealerRequiredPageShell from 'app/components/ReusableUIComponents/HighLevelComponents/DealerRequiredPageShell';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import FilterTrayMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';
import { DealerPageHeaderEntityInfo } from 'app/components/ReusableUIComponents/LowLevelComponents/PageHeaderEntityInfo';
import { withResourceProtection } from 'app/components/ReusableUIComponents/HOCs/ResourceProtected/withResourceProtection';
import { MONITOR_RESOURCE_NURTURING_CANDIDATE_AUDIT } from 'app/pages/MonitorPage/utils/monitorResourceIds';

import ExecutionLogDetailModal from './components/ExecutionLogDetailModal';
import { useNurturingCandidateAuditPage } from './hooks/useNurturingCandidateAuditPage';
import type { ExecutionLogRow } from './types';

const NurturingCandidateAuditPage = () => {
  const [quickFilterAnchorEl, setQuickFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [selectedExecutionLog, setSelectedExecutionLog] =
    useState<ExecutionLogRow | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const {
    breadcrumbsList,
    hasDealer,
    dealerTitle,
    dealerId,
    executionLogGridType,
    gridFilterFromState,
    isQuickFilterOpen,
    setIsQuickFilterOpen,
    filterTray,
  } = useNurturingCandidateAuditPage();

  const handleOpenQuickFilter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setQuickFilterAnchorEl(event.currentTarget);
      setIsQuickFilterOpen(true);
    },
    [setIsQuickFilterOpen],
  );

  const handleCloseQuickFilter = useCallback(() => {
    setIsQuickFilterOpen(false);
    setQuickFilterAnchorEl(null);
  }, [setIsQuickFilterOpen]);

  const handleCellClick = useCallback((cell: { field?: string; row?: ExecutionLogRow }) => {
    if (cell?.field === '_id' && cell?.row) {
      setSelectedExecutionLog(cell.row);
      setIsDetailModalOpen(true);
    }
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedExecutionLog(null);
  }, []);

  const gridKey = [
    dealerId ?? '',
    gridFilterFromState?.run_id ?? '',
    gridFilterFromState?.lead_id ?? '',
    gridFilterFromState?.shopper_id ?? '',
    gridFilterFromState?.action_id ?? '',
    JSON.stringify(filterTray.executionLogFilterModel ?? {}),
  ].join('|');

  return (
    <>
      <Helmet>
        <title>Nurturing Candidate Audit</title>
        <meta
          name="description"
          content="Audit nurturing candidate picker execution logs for the selected dealership"
        />
      </Helmet>
      <DealerRequiredPageShell
        hasDealer={hasDealer}
        breadcrumbsList={breadcrumbsList}
        pageTitle="Nurturing Candidate Audit"
        headerSubtitle={
          hasDealer ? (
            <DealerPageHeaderEntityInfo
              title={dealerTitle}
              dealerId={dealerId}
            />
          ) : undefined
        }
        contentVariant="fluid"
      >
        {executionLogGridType ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: 1440,
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              backgroundColor: 'background.paper',
              borderRadius: 1.5,
              boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                pt: 1.5,
                px: 1.5,
                pb: 1,
                flexShrink: 0,
              }}
            >
              <Badge
                badgeContent={
                  filterTray.totalActiveCount > 0
                    ? filterTray.totalActiveCount
                    : undefined
                }
                color="primary"
                sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenQuickFilter}
                  startIcon={<FilterListIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Quick Filters
                </Button>
              </Badge>
            </Box>
            <DataGridMAIQ
              key={gridKey}
              type={executionLogGridType}
              onCellClick={handleCellClick}
            />
            <ExecutionLogDetailModal
              open={isDetailModalOpen}
              row={selectedExecutionLog}
              onClose={handleCloseDetailModal}
            />
            <FilterTrayMAIQ
              isLoading={filterTray.isLoading}
              open={isQuickFilterOpen}
              onClose={handleCloseQuickFilter}
              anchorEl={quickFilterAnchorEl}
              categories={filterTray.categories}
              activeCategoryId={filterTray.activeCategoryId}
              onActiveCategoryChange={filterTray.setActiveCategoryId}
              optionsForActiveCategory={filterTray.optionsForActiveCategory}
              selectedByCategory={filterTray.selectedByCategory}
              onSelectionChange={filterTray.onSelectionChange}
              totalActiveCount={filterTray.totalActiveCount}
              onClearAllFilters={filterTray.onClearAllFilters}
              onClearActiveCategory={filterTray.onClearActiveCategory}
              onClearAllInPanel={filterTray.onClearAllInPanel}
            />
          </Box>
        ) : null}
      </DealerRequiredPageShell>
    </>
  );
};

export default withResourceProtection(
  NurturingCandidateAuditPage,
  MONITOR_RESOURCE_NURTURING_CANDIDATE_AUDIT,
);
